<template>
  <b-card class="mb-3">
    <template #header>
      <div>
        <i class="fa fa-ellipsis-v mr-2 drag-handle"></i>
        <i class="fa fa-cogs"></i> <strong>Account Actions</strong>
      </div>
    </template>

    <b-form-group label="In-Game Notification" description="This message will be shown to the user via popup notification.">
      <b-input-group>
        <b-input-group-prepend is-text>
          <i class="fa fa-comment"></i>
        </b-input-group-prepend>
        <b-form-input
          v-model="loginMessage"
          :disabled="servers[userData.server].readonly || userLock"
          placeholder="Type a message..."
        />
        <b-input-group-append>
          <b-button
            variant="primary"
            @click="setLoginMessage"
            :disabled="servers[userData.server].readonly || !hasPermission(UserLoginMessage) || userLock || loginMessage.length === 0"
          >
            Send Message
          </b-button>
        </b-input-group-append>
      </b-input-group>
    </b-form-group>

    <hr />

    <div class="d-flex flex-wrap justify-content-end">
      <template v-if="servers[userData.server].readonly">
        <b-button
          class="m-1"
          v-if="userData.merge_token"
          :disabled="userLock"
          variant="info"
          :href="'https://pokemonrevolution.net/merge/?token=' + userData.merge_token"
          target="_blank"
        >
          <i class="fa fa-bolt"></i> Force Merge
        </b-button>
      </template>

      <b-button
        class="m-1"
        v-if="userData.server === 'silver'"
        variant="warning"
        :disabled="!hasPermission(UserUnmerge) || userLock"
        @click="showUnmergeModal"
      >
        <i class="fa fa-user-times"></i> Unmerge
      </b-button>

      <b-button
        v-if="(userData.server === 'gold' || userData.server === 'silver') && !servers[userData.server].readonly"
        class="m-1"
        variant="info"
        :disabled="!hasPermission(UserServerTransfer) || userLock"
        @click="transferServer(userData.server === 'gold' ? 'silver' : 'gold')"
      >
        <i class="fa fa-exchange"></i> Server Swap
      </b-button>

      <b-button
        class="m-1"
        variant="primary"
        :disabled="!hasPermission(MoveClothesToInventory) || userLock"
        @click="moveClothesToInventory"
      >
        <i class="fa fa-eject"></i> Clothes ➝ Inventory
      </b-button>

      <b-button
        class="m-1"
        variant="danger"
        :disabled="!hasPermission(UserReset) || userLock"
        @click="showModal"
      >
        <i class="fa fa-rotate-left"></i> Reset Account
      </b-button>
    </div>

    <!-- Modals moved here for encapsulation -->
    <b-modal title="Danger Zone: Reset Account" v-model="resetModal" header-bg-variant="danger" header-text-variant="white">
      <div class="text-center text-danger mb-3">
        <i class="fa fa-exclamation-triangle fa-3x"></i>
      </div>
      <p class="text-center font-weight-bold">Unexpected bad things will happen if you don't read this!</p>
      <p>This action <b>cannot</b> be undone. This will permanently delete all pokemon, money, coins, items, variables and pokedex entries from <b>{{userData.username}}</b>'s account.</p>
      <b-form-group label="Type username to confirm:">
        <b-form-input v-model="resetInput" ref="resetUsername" autofocus />
      </b-form-group>
      <template #modal-footer>
        <b-button variant="danger" block :disabled="resetInput.toLowerCase() !== userData.username.toLowerCase()" @click="wipeAccount">
          I understand the consequences, reset this account
        </b-button>
      </template>
    </b-modal>

    <b-modal title="Unmerge Account" v-model="unmergeModal" header-bg-variant="warning" header-text-variant="white">
      <div class="text-center text-warning mb-3">
        <i class="fa fa-exclamation-circle fa-3x"></i>
      </div>
      <p class="text-center font-weight-bold">Unexpected bad things will happen if you don't read this!</p>
      <p>This action <b>cannot</b> be undone. This will unmerge <b>{{userData.username}}</b>'s account on <b>Silver</b>. Everything will be deleted.</p>
      <b-form-group label="Type username to confirm:">
        <b-form-input v-model="unmergeInput" ref="unmergeUsername" autofocus />
      </b-form-group>
      <template #modal-footer>
        <b-button variant="danger" block :disabled="unmergeInput.toLowerCase() !== userData.username.toLowerCase()" @click="unmergeUser">
          Confirm Unmerge
        </b-button>
      </template>
    </b-modal>

  </b-card>
</template>

<script>
import { mapActions } from 'vuex'
import iziToast from 'izitoast'

export default {
  name: 'AccountActionsCard',
  props: {
    userData: { type: Object, required: true },
    servers: { type: Object, required: true },
    userLock: { type: Boolean, default: false }
  },
  data() {
    return {
      loginMessage: '',
      resetModal: false,
      resetInput: '',
      unmergeModal: false,
      unmergeInput: ''
    }
  },
  methods: {
    ...mapActions(['setLock']),
    showModal () {
      this.resetInput = ''
      this.resetModal = true
    },
    showUnmergeModal () {
      this.resetInput = ''
      this.unmergeModal = true
    },
    wipeAccount () {
      this.resetModal = false
      this.emit('wipeUser', {'id': this.userData.id, 'server': this.userData.server})
    },
    unmergeUser () {
      this.unmergeModal = false
      this.emit('unmergeUser', this.userData.username)
    },
    moveClothesToInventory () {
      this.emit('moveClothesToInventory', {
        username: this.userData.username,
        server: this.userData.server
      })
      this.setLock(true)
    },
    setLoginMessage () {
      this.emit('setLoginMessage', {
        id: this.userData.id,
        value: this.loginMessage,
        server: this.userData.server
      })
      this.setLock(true)
    },
    capitalizeFirstLetter (string) {
      return string.charAt(0).toUpperCase() + string.slice(1)
    },
    transferServer (targetServer) {
      let self = this
      iziToast.question({
        timeout: 20000,
        close: true,
        overlay: true,
        toastOnce: true,
        id: 'questionTransfer',
        zindex: 99999,
        message: 'Are you sure that you want to transfer <strong>' + this.userData.username + '</strong> to <strong>' + self.capitalizeFirstLetter(targetServer) + '</strong>?',
        position: 'center',
        buttons: [
          ['<button><b>YES</b></button>', function (instance, toast) {
            iziToast.hide({}, toast)
            self.TransferProgress()
            self.setLock(true)
            self.emit('serverTransfer', {
              username: self.userData.username,
              target_server: targetServer,
              source_server: self.userData.server
            })
          }, true]
        ]
      })
    },
    TransferProgress () {
      iziToast.info({
        title: 'Transfer in progress!',
        message: 'This may take several seconds, please be patient.',
        transitionIn: 'bounceInLeft'
      })
    }
  }
}
</script>
